import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiHeart,
  FiBook,
  FiUsers,
  FiAward,
  FiClock,
  FiCheckCircle,
  FiStar,
  FiTrendingUp,
} from "react-icons/fi";

// Import images
import heroImage from "../assets/schulungen/schu4.JPEG";
import detailImage1 from "../assets/homeAssets/aboutMe2.JPG";
import detailImage2 from "../assets/schulungen/schu4.JPEG";

// --- Styled Components ---

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #fcf9f7 0%, #f5f0eb 100%);
  color: #1a1a1a;
`;

const Section = styled.section`
  padding: 120px 5%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const DecorativeText = styled.div`
  position: absolute;
  font-family: "Lovelace", serif;
  font-size: 15vw;
  color: rgba(155, 128, 103, 0.03);
  z-index: 0;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
`;

// Hero Section
const HeroSection = styled.section`
  height: 60vh;
  min-height: 500px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeroImage = styled(motion.div)`
  position: absolute;
  inset: 0;
  background-image: url(${heroImage});
  background-size: cover;
  background-position: center;
  filter: brightness(0.6);
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(99, 86, 78, 0.4) 0%,
    rgba(142, 116, 121, 0.3) 100%
  );
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 900px;
  padding: 0 20px;
`;

const HeroTitle = styled(motion.h1)`
  font-family: "Lovelace", serif;
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: 400;
  margin: 0 0 20px;
  letter-spacing: -1px;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
  font-style: italic;
`;

const HeroSubtitle = styled(motion.p)`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.5rem;
  font-style: italic;
  opacity: 0.95;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

// Text Block
const TextBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 800px;
  text-align: center;
  align-items: center;
  position: relative;
  z-index: 2;

  h2 {
    font-family: "Lovelace", serif;
    font-style: italic;
    font-size: 3.5rem;
    line-height: 1.2;
    color: #4a3a3d;
    margin: 0;
  }

  p {
    font-family: "Montserrat", serif;
    font-size: 1.1rem;
    line-height: 1.8;
    color: #a39396;
    margin: 0;
  }

  .accent-line {
    width: 80px;
    height: 2px;
    background: #9b8067;
    opacity: 0.3;
    margin: 20px 0;
  }
`;

// Main Benefits Grid
const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  width: 100%;
  max-width: 1300px;
  margin: 60px 0;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  padding: 50px 40px;
  border-radius: 40px;
  border: 1px solid rgba(155, 128, 103, 0.15);
  box-shadow: 0 20px 40px rgba(99, 86, 78, 0.08);
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #9b8067, #8e7479, #9b8067);
    opacity: 0.3;
  }
`;

const BenefitIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: #9b8067;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.2rem;
  margin-bottom: 10px;
  box-shadow: 0 15px 30px rgba(155, 128, 103, 0.2);
`;

const BenefitTitle = styled.h3`
  font-family: "Lovelace", serif;
  font-style: italic;
  font-size: 2rem;
  color: #4a3a3d;
  margin: 0;
  line-height: 1.2;
`;

const BenefitText = styled.p`
  font-family: "Montserrat", serif;
  font-size: 1rem;
  line-height: 1.8;
  color: #a39396;
  margin: 0;
`;

const BenefitFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  color: #8e7479;
  font-size: 0.9rem;
  font-family: "Montserrat", serif;

  svg {
    color: #9b8067;
    font-size: 1rem;
  }
`;

// Split Content Section
const SplitContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  max-width: 1200px;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
`;

const ImageContainer = styled(motion.div)`
  width: 100%;
  height: 600px;
  border-radius: 40px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(99, 86, 78, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const LeftTextBlock = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 25px;

  h3 {
    font-family: "Lovelace", serif;
    font-style: italic;
    font-size: 2.5rem;
    color: #4a3a3d;
    margin: 0;
  }

  p {
    font-family: "Montserrat", serif;
    font-size: 1rem;
    line-height: 1.8;
    color: #a39396;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 10px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #63564e;
  font-family: "Montserrat", serif;
  font-size: 1rem;

  svg {
    color: #9b8067;
    font-size: 1.2rem;
  }
`;

// Support Section
const SupportSection = styled(motion.div)`
  margin-top: 80px;
  padding: 50px;
  background: rgba(155, 128, 103, 0.1);
  border-radius: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  border: 1px solid rgba(155, 128, 103, 0.2);
  backdrop-filter: blur(10px);
  width: 100%;
  max-width: 1000px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 40px;
  }
`;

const SupportIcon = styled.div`
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9b8067;
  font-size: 2rem;
  box-shadow: 0 15px 30px rgba(155, 128, 103, 0.15);
`;

const SupportText = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.5rem;
  color: #63564e;
  font-style: italic;
  max-width: 600px;
  line-height: 1.5;
`;

const CTASection = styled(motion.div)`
  margin-top: 60px;
  display: flex;
  gap: 30px;

  @media (max-width: 568px) {
    flex-direction: column;
  }
`;

const CTAButton = styled(Link)`
  padding: 18px 45px;
  background: ${(props) => (props.primary ? "#9b8067" : "transparent")};
  color: ${(props) => (props.primary ? "white" : "#9b8067")};
  border: 1px solid #9b8067;
  border-radius: 50px;
  font-family: "Montserrat", sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${(props) => (props.primary ? "#8e7479" : "#9b8067")};
    color: white;
  }
`;

// Stats Section
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin: 60px 0;
  width: 100%;
  max-width: 1000px;

  @media (max-width: 568px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
`;

const StatNumber = styled.div`
  font-family: "Lovelace", serif;
  font-size: 3rem;
  color: #9b8067;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-family: "Montserrat", serif;
  font-size: 0.9rem;
  color: #8e7479;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

// --- Animation Variants ---
const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

// --- Component ---
const Benefits = () => {
  const benefits = [
    {
      icon: <FiBook />,
      title: "Theorie & Praxis",
      text: "Fundiertes Wissen zu Hygiene, Materialkunde, Styling und Technik. Schritt für Schritt durch den kompletten Behandlungsablauf.",
      features: ["Anatomie Grundlagen", "Materialkunde", "Klebetechniken"],
    },
    {
      icon: <FiTrendingUp />,
      title: "Business & Marketing",
      text: "Wertvolle Einblicke in die Selbstständigkeit – von der Kundenberatung bis zum erfolgreichen Aufbau deines eigenen Businesses.",
      features: ["Kundenakquise", "Social Media", "Preisgestaltung"],
    },
    {
      icon: <FiHeart />,
      title: "Nachhaltige Unterstützung",
      text: "Auch nach deiner Schulung stehe ich dir bei Fragen und Hilfe zur Seite. Gemeinsam wachsen und erfolgreich sein.",
      features: ["WhatsApp Support", "Follow-up Fragen", "Community"],
    },
  ];

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroImage
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        />
        <HeroOverlay />
        <HeroContent
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <HeroTitle>Deine Vorteile</HeroTitle>
          <HeroSubtitle>
            Was dich in meinen Schulungen erwartet – und warum es sich lohnt
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Main Benefits Section */}
      <Section>
        <DecorativeText style={{ top: "10%", right: "0" }}>
          BENEFITS
        </DecorativeText>

        <TextBlock
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <h2>Was erwartet dich?</h2>
          <div className="accent-line" />
          <p>
            In meinen Schulungen erhältst du nicht nur theoretisches Wissen,
            sondern vor allem praktische Erfahrung für deinen erfolgreichen
            Start als Wimpernstylistin.
          </p>
        </TextBlock>

        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariants}
              transition={{ delay: index * 0.1 }}
              whileHover={{
                y: -10,
                boxShadow: "0 40px 80px rgba(99, 86, 78, 0.15)",
                transition: { duration: 0.3 },
              }}
            >
              <BenefitIcon
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                {benefit.icon}
              </BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitText>{benefit.text}</BenefitText>
              {benefit.features.map((feature, idx) => (
                <BenefitFeature key={idx}>
                  <FiCheckCircle /> {feature}
                </BenefitFeature>
              ))}
            </BenefitCard>
          ))}
        </BenefitsGrid>

        {/* Stats */}
        <StatsGrid>
          <StatCard
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
          >
            <StatNumber>100%</StatNumber>
            <StatLabel>Praxisorientiert</StatLabel>
          </StatCard>
          <StatCard
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
            transition={{ delay: 0.1 }}
          >
            <StatNumber>4.9</StatNumber>
            <StatLabel>Bewertungen</StatLabel>
          </StatCard>
          <StatCard
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
            transition={{ delay: 0.2 }}
          >
            <StatNumber>50+</StatNumber>
            <StatLabel>Absolventinnen</StatLabel>
          </StatCard>
        </StatsGrid>
      </Section>

      {/* Split Section: Theory in Detail */}
      <Section style={{ paddingTop: 0 }}>
        <SplitContent>
          <LeftTextBlock
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
          >
            <h3>Von der Vorbereitung bis zur Nachbehandlung</h3>
            <p>
              Du wirst Schritt für Schritt durch den kompletten
              Behandlungsablauf geführt:
            </p>
            <FeatureList>
              <FeatureItem>
                <FiCheckCircle /> Vorbereitung & Hygiene
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Materialkunde & Technik
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Styling & Design
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Nachbehandlung & Pflege
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Kundenberatung & Kommunikation
              </FeatureItem>
            </FeatureList>
          </LeftTextBlock>
          <ImageContainer
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src={detailImage1} alt="Behandlung im Detail" />
          </ImageContainer>
        </SplitContent>
      </Section>

      {/* Split Section: Business Insights */}
      <Section>
        <SplitContent style={{ direction: "rtl" }}>
          <LeftTextBlock
            style={{ direction: "ltr" }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
          >
            <h3>Business & Marketing Einblicke</h3>
            <p>
              Ergänzt wird die Schulung durch wertvolle Einblicke in die
              Selbstständigkeit:
            </p>
            <FeatureList>
              <FeatureItem>
                <FiCheckCircle /> Business Aufbau & Planung
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Marketing Strategien
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Social Media Präsenz
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Kundenakquise & Bindung
              </FeatureItem>
              <FeatureItem>
                <FiCheckCircle /> Preisgestaltung & Angebote
              </FeatureItem>
            </FeatureList>
          </LeftTextBlock>
          <ImageContainer
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src={detailImage2} alt="Business und Marketing" />
          </ImageContainer>
        </SplitContent>
      </Section>

      {/* Support Section */}
      <Section>
        <SupportSection
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <SupportIcon>
            <FiHeart />
          </SupportIcon>
          <SupportText>
            Auch nach deiner Schulung steht dir meine Unterstützung bei Fragen
            und Hilfe zur Verfügung.
          </SupportText>
        </SupportSection>

        <CTASection
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
          transition={{ delay: 0.2 }}
        >
          <CTAButton to="/schulungen" primary>
            <FiAward /> Zu den Schulungen
          </CTAButton>
          <CTAButton to="/kontakt">
            <FiUsers /> Kontakt aufnehmen
          </CTAButton>
        </CTASection>
      </Section>
    </PageContainer>
  );
};

export default Benefits;
